/**
 * NDT invoices (batch 5, fieldiq-api#1166): the status skin, who may press
 * what, the levied tax rows and their provenance, the lines grouped by ticket,
 * and every refusal the invoice doors answer with, as a plain sentence.
 *
 * NOTHING HERE ADDS OR COMPUTES TAX. Subtotal, each levied tax, and the total
 * are the API's, stored at generation; this file only words and groups them.
 */
import { formatCalendarDate } from './calendar-date';
import { fmtTaxRate } from './display-labels';
import { NDT_UNKNOWN_SKIN, type NdtSkin } from './ndt-inspection';
import type { NdtInvoice, NdtInvoiceLine, NdtInvoiceStatus } from '../types/ndt-invoices';

/**
 * Draft amber (not yet with the customer), sent blue, approved blue (the
 * customer accepted it), paid green, void slate.
 */
export const NDT_INVOICE_STATUS_SKIN: Record<NdtInvoiceStatus, NdtSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  sent: { label: 'Sent', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  paid: { label: 'Paid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtInvoiceStatusSkin = (v: string | null | undefined): NdtSkin =>
  v && Object.prototype.hasOwnProperty.call(NDT_INVOICE_STATUS_SKIN, v) ? NDT_INVOICE_STATUS_SKIN[v as NdtInvoiceStatus] : NDT_UNKNOWN_SKIN;

/** NDT_INVOICE_READ (lib/ndt-roles.ts): admin, office_staff, dispatcher. */
export const NDT_INVOICE_READ_ROLES = ['admin', 'office_staff', 'dispatcher'] as const;
/** NDT_INVOICE_WRITE: generate, send, void, mark paid — admin, office_staff. */
export const NDT_INVOICE_WRITE_ROLES = ['admin', 'office_staff'] as const;
export const canReadNdtInvoices = (role: string | null | undefined) => !!role && (NDT_INVOICE_READ_ROLES as readonly string[]).includes(role);
export const canWriteNdtInvoices = (role: string | null | undefined) => !!role && (NDT_INVOICE_WRITE_ROLES as readonly string[]).includes(role);

/** Statuses an invoice can still be sent, voided or marked paid from. */
export const ndtInvoiceIsLive = (status: string | null | undefined) => status === 'draft' || status === 'sent' || status === 'approved';
/** Mark paid: once it is with the customer (sent or approved), never a draft. */
export const ndtInvoiceCanMarkPaid = (status: string | null | undefined) => status === 'sent' || status === 'approved';

/** The fallbacks that hold a send (lib/company-tax.ts invoiceTaxFallbackBlocked). */
export const NDT_SEND_BLOCKING_FALLBACK = ['jurisdiction_absent', 'not_yet_effective'];
export const ndtInvoiceSendBlocked = (inv: Pick<NdtInvoice, 'taxFallbackReason' | 'taxExempt'>) =>
  NDT_SEND_BLOCKING_FALLBACK.includes(inv.taxFallbackReason ?? '') && !inv.taxExempt;

/** The levied tax rows, as the PDF prints them: a stored label with a percent above 0. */
export function ndtLeviedTaxRows(inv: NdtInvoice) {
  return [
    { label: inv.taxRate1Label, percent: inv.taxRate1Percent, amount: inv.taxRate1Amount },
    { label: inv.taxRate2Label, percent: inv.taxRate2Percent, amount: inv.taxRate2Amount },
  ].filter((r) => !!r.label && r.percent !== null && r.percent > 0 && r.amount !== null);
}

/** "GST (5%)", from the stored label and percent; `—` without a label. */
export function ndtTaxRowLabel(label: string | null | undefined, percent: number | null | undefined): string {
  if (!label) return '—';
  return percent === null || percent === undefined ? label : `${label} (${fmtTaxRate(percent)})`;
}

/** Where the tax came from, from the provenance stored at generation — never re-derived. Unknown → `—`. */
export function ndtTaxProvenanceText(
  inv: Pick<NdtInvoice, 'taxRateSource' | 'taxFallbackReason' | 'jurisdiction' | 'taxRateEffectiveFrom' | 'taxExempt'>,
): string {
  const where = inv.jurisdiction || 'the province';
  if (inv.taxExempt) return 'Customer is tax exempt';
  switch (inv.taxRateSource) {
    case 'jurisdiction_row': {
      const from = formatCalendarDate(inv.taxRateEffectiveFrom);
      return from ? `${where} rate, in effect from ${from}` : `${where} rate`;
    }
    case 'company_columns':
      switch (inv.taxFallbackReason) {
        case 'jurisdiction_unknown':
          return 'Company tax settings (no province on the job)';
        case 'jurisdiction_absent':
          return `No ${where} rate set up, company default used`;
        case 'not_yet_effective':
          return `${where} rate not in effect yet, company default used`;
        default:
          return 'Company tax settings';
      }
    case 'none':
      return 'No tax applies';
    default:
      return '—';
  }
}

/** The invoice's lines, in the API's order, under the ticket each came from. */
export function ndtInvoiceLineGroups(inv: Pick<NdtInvoice, 'lines'>) {
  const groups: { ticketId: string; ticketNumber: string; lines: NdtInvoiceLine[] }[] = [];
  for (const l of inv.lines) {
    let g = groups.find((x) => x.ticketId === l.ticketId);
    if (!g) {
      g = { ticketId: l.ticketId, ticketNumber: l.ticketNumber, lines: [] };
      groups.push(g);
    }
    g.lines.push(l);
  }
  return groups;
}

/** The API's own filename (GET /invoices/:id/pdf Content-Disposition): NDI-0001.pdf, -VOID. */
export const ndtInvoicePdfFilename = (inv: { invoiceNumber: string; status: string }) =>
  `${inv.invoiceNumber}${inv.status === 'void' ? '-VOID' : ''}.pdf`;

/**
 * EVERY `code` the NDT invoice doors answer with (routes/ndt-inspection-invoices.routes.ts,
 * lib/ndt-invoice.ts, the shared customer-exemption refusal, and api#1168's void),
 * as a plain sentence. The tests pin this list, so a code added there and not
 * here fails a test rather than falling back silently.
 *
 * Codes in PREFER_API_TEXT carry the API's specifics (ticket numbers, the
 * provinces, the customer's name) and show the API's `error` when it sent one.
 */
export const NDT_INVOICE_REFUSAL_MESSAGES: Record<string, string> = {
  // generate
  selection_required: 'Choose a job, or pick the field tickets to invoice.',
  invalid_id: 'That job could not be found. Refresh the page and try again.',
  tickets_required: 'Choose between 1 and 100 field tickets to invoice.',
  ticket_listed_twice: 'A field ticket is listed twice. Choose each ticket once.',
  date_invalid: 'The due date is not a valid date.',
  text_too_long: 'That text is too long.',
  no_approved_tickets: 'This job has no approved field tickets to invoice.',
  ticket_not_approved: 'Only an approved field ticket can be invoiced.',
  ticket_already_invoiced: 'A ticket is already on an invoice. Void that invoice to bill it again.',
  ticket_unpriced: 'A ticket has a line with no price. Price it before invoicing.',
  tickets_different_customers: 'These tickets belong to different customers. One invoice can only bill one customer.',
  tickets_mixed_currency: 'These tickets are in more than one currency. One invoice can only use one currency.',
  tickets_different_jurisdictions: 'These tickets were worked in different provinces or states. One invoice can only cover one.',
  tax_unconfigured: 'Tax is not set up for this province or state. Set it in Settings, Regional & Tax.',
  FULL_TAX_EXEMPTION_REFUSED: 'This customer is marked fully tax exempt, which an invoice cannot carry. Check the customer record.',
  PST_EXEMPTION_NUMBER_REQUIRED: "This customer is PST exempt but has no exemption certificate number. Add it on the customer record.",
  invoice_number_contended: 'Another invoice was being numbered at the same moment. Try again.',
  ticket_state_changed: 'A field ticket changed while this was being saved. Refresh and try again.',
  // send
  to_invalid: 'That is not a valid email address.',
  cc_invalid: 'One of the CC addresses is not valid.',
  invoice_not_sendable: 'This invoice is void or already paid, so it cannot be sent.',
  tax_fallback_blocked: "This invoice was taxed at the company's default rate because the province's rate is not set up, so it cannot be sent.",
  no_billing_email: 'The customer has no accounting or main contact email. Type an address to send to.',
  email_not_sent: 'The email was not sent. The invoice is unchanged.',
  // void
  void_not_available: 'Voiding NDT invoices is coming shortly. For now this invoice stays as it is; contact support if it must be withdrawn.',
  reason_required: 'A reason is required to void an invoice.',
  invoice_already_void: 'This invoice is already void.',
  invoice_not_voidable: 'A paid invoice cannot be voided.',
  // any door
  body_required: 'Nothing was sent. Fill in the form and try again.',
  unknown_field: 'This form sent a detail the system does not recognize. Refresh the page and try again.',
  invalid_request: 'The request was not valid.',
  not_found: 'This invoice or one of its tickets was not found. It may have been deleted.',
};

const PREFER_API_TEXT = new Set([
  'no_approved_tickets',
  'ticket_not_approved',
  'ticket_already_invoiced',
  'ticket_unpriced',
  'tickets_mixed_currency',
  'tickets_different_jurisdictions',
  'tax_unconfigured',
  'tax_fallback_blocked',
  'no_billing_email',
  'email_not_sent',
  'to_invalid',
  'cc_invalid',
  'text_too_long',
  'invalid_request',
]);

export interface NdtInvoiceRefusal {
  status: number | null;
  code: string | null;
  message: string;
}

/** A refused invoice write as a sentence. `void_not_available` is always this file's words, never the raw code. */
export function ndtInvoiceRefusal(err: unknown, generic: string): NdtInvoiceRefusal {
  const r = (err as { response?: { status?: unknown; data?: Record<string, unknown> } })?.response;
  const d = (r?.data ?? {}) as Record<string, unknown>;
  const status = typeof r?.status === 'number' ? r.status : null;
  const code = typeof d.code === 'string' ? d.code : status === 404 ? 'not_found' : null;
  const apiError = typeof d.error === 'string' && d.error && d.error !== 'Not found' ? d.error : null;
  const known = !!code && Object.prototype.hasOwnProperty.call(NDT_INVOICE_REFUSAL_MESSAGES, code);
  const message =
    known && !(PREFER_API_TEXT.has(code as string) && apiError) ? NDT_INVOICE_REFUSAL_MESSAGES[code as string] : apiError ?? generic;
  return { status, code, message };
}
